import {
  DepositPaymentEvent,
  PaymentEventEnum,
  SimpleCompanyPaymentTerms,
  useApiTradeV1TradeCompaniesPaymentTermsList,
} from 'api';
import { useMemo } from 'react';

import {
  AutocompleteField,
  AutocompleteFieldProps,
  type AutocompleteOption,
} from '../AutocompleteField/AutocompleteField';

export type PaymentTermOption = AutocompleteOption & {
  term: SimpleCompanyPaymentTerms | null;
  isCustom?: boolean;
};

export type CompanyPaymentTermsAutocompleteProps = {
  name: string;
  label: string;
  tradeCompanyId?: number;
  onTermSelect?: (term: SimpleCompanyPaymentTerms | null) => void;
  required?: boolean;
  disabled?: boolean;
  infoTooltip?: string;
  'data-testid'?: string;
  includeCustomOption?: boolean;
  onOptionsLoaded?: (options: PaymentTermOption[]) => void;
} & Omit<AutocompleteFieldProps<PaymentTermOption>, 'options'>;

const getDepositPaymentEventLabel = (eventCode: string | null): string => {
  if (!eventCode) return '';

  switch (eventCode) {
    case DepositPaymentEvent.SIGNED_PO:
      return 'Upon receipt of signed PO';
    case DepositPaymentEvent.SIGNED_PO_AND_PREINSPECTION:
      return 'Upon receipt of signed PO and successful Pre-inspection';
    case DepositPaymentEvent.COLLECTION:
      return 'Collection from Supplier';
    case DepositPaymentEvent.PREINSPECTION_AND_COLLECTION:
      return 'After successful Pre-inspection + Collection from Supplier';
    default:
      return eventCode;
  }
};

const getPaymentEventLabel = (eventCode: string): string => {
  switch (eventCode) {
    case PaymentEventEnum.CL:
      return 'Collection from supplier';
    case PaymentEventEnum.XP:
      return 'Export';
    case PaymentEventEnum.BL:
      return 'BOL date';
    case PaymentEventEnum.DA:
      return 'Destination port arrival';
    case PaymentEventEnum.SD:
      return 'Site delivery';
    case PaymentEventEnum.IA:
      return 'ICD arrival';
    default:
      return eventCode;
  }
};

const formatDaysOffset = (days: number | null): string => {
  if (days === null) return '';
  return days < 0 ? `${Math.abs(days)} days before` : `${days} days after`;
};

export const createPaymentTermLabel = (term: SimpleCompanyPaymentTerms): string => {
  const depositInfo = term.deposit_percentage
    ? `${term.deposit_percentage}% deposit ${
        term.deposit_payment_event
          ? `(${formatDaysOffset(term.deposit_event_offset_days)} ${getDepositPaymentEventLabel(term.deposit_payment_event)})`
          : ''
      }`
    : 'No deposit';

  const remainderInfo = `Remainder ${formatDaysOffset(term.due_date_offset_days)} ${getPaymentEventLabel(term.payment_event)}`;

  return term.is_preferred ? `${depositInfo} | ${remainderInfo} (Preferred)` : `${depositInfo} | ${remainderInfo}`;
};

export const CompanyPaymentTermsAutocomplete = ({
  name,
  label,
  tradeCompanyId,
  required = false,
  disabled = false,
  infoTooltip,
  includeCustomOption = true,
  onOptionsLoaded,
  ...rest
}: CompanyPaymentTermsAutocompleteProps) => {
  const { data, isLoading } = useApiTradeV1TradeCompaniesPaymentTermsList(tradeCompanyId || 0, undefined, {
    query: {
      enabled: Boolean(tradeCompanyId),
    },
  });

  const paymentTermOptions = useMemo(() => {
    const terms = data?.results || [];
    const options: PaymentTermOption[] = terms.map((term) => {
      return {
        label: createPaymentTermLabel(term),
        term,
        value: term.id.toString(),
      };
    });

    if (includeCustomOption) {
      options.push({
        isCustom: true,
        label: 'Custom',
        term: null,
        value: 'custom',
      });
    }

    if (onOptionsLoaded && options.length > 0) {
      onOptionsLoaded(options);
    }

    return options;
  }, [data?.results, includeCustomOption, onOptionsLoaded]);

  return (
    <AutocompleteField<PaymentTermOption>
      name={name}
      label={label}
      options={paymentTermOptions}
      loading={isLoading}
      disabled={disabled || isLoading}
      required={required}
      infoTooltip={infoTooltip}
      valueField="value"
      {...rest}
    />
  );
};
